import { GridCellProps } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import TextInput from 'src/components/TextInput';
import { isButtonEnabled } from 'src/components/design-system/Button';
import useDelayedInputOnchange from 'src/hooks/useDelayedInputOnchange';
import { round } from 'src/utils';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const DescriptionTextField: FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const getMatchingOcrLineItem = useContextSelector(
    InvoiceContext,
    (value) => value.getMatchingOcrLineItem
  );
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);

  const error = useMemo(() => dataItem?.errors?.description, [dataItem?.errors?.description]);
  const { handleChange, value } = useDelayedInputOnchange(props, true);

  const matchingItem = getMatchingOcrLineItem(dataItem.receiptLineItemId);
  const ocrConfidence = matchingItem.reduce(
    (acc, item) => (acc += parseFloat(item?.item?.itemDescription?.confidence || '0')),
    0
  );
  const averageOcrConfidence = round(ocrConfidence / matchingItem.length, 2);
  const isEdited = useMemo(
    () =>
      matchingItem[0]?.item?.itemDescription?.value?.toLowerCase()?.trim() !==
      value.toLowerCase()?.trim(),
    [matchingItem, value]
  );

  const matchItemIndex = matchingItem.map(
    (item) => Number(item.item?.itemSequenceNumber?.value) - 1
  );

  const handleHover = useCallback(() => {
    handleHoverOcrField('itemDescription', matchItemIndex);
  }, [handleHoverOcrField, matchItemIndex]);
  const tooltip = value.replace('\n', '');

  return (
    <GridCellBase {...props} className={!error.length ? '' : 'bg-errorcell'} disableToolTip>
      <div className="flex flex-row items-center justify-between gap-1">
        <div className="truncate" data-tooltip-id={'global-tooltip'} data-tooltip-content={tooltip}>
          {Number(dataItem?.poQty) === 0 &&
          isButtonEnabled(dataItem.canDelete) &&
          !isInvoiceEditDisabled ? (
            <TextInput
              type={'text'}
              value={value}
              onChange={handleChange}
              valid={!error?.length}
              onFocus={handleHover}
              ocr-data={`itemDescription:${matchItemIndex}`}
            />
          ) : (
            <span>{value}</span>
          )}
        </div>
        {Number(dataItem?.poQty) === 0 && initialInvoice.ocrGenerated && ocrConfidence !== 0 && (
          <OcrIndicator
            isEdited={isEdited}
            ocrConfidence={`${averageOcrConfidence}%`}
            onHover={handleHover}
          />
        )}
      </div>
    </GridCellBase>
  );
};

export default DescriptionTextField;
